import React from "react";
import Section from "./Section";
import * as Dialog from "@radix-ui/react-dialog";

import {
  Flex,
  Paragraph,
  Heading,
  Separator,
  CodeHighlight,
  Button,
  IconButton,
  Avatar,
  Table,
  Card,
} from "../src/index";
import ReactLive from "./ReactLive";
import {
  Cross2Icon,
  SizeIcon,
  CopyIcon,
  BellIcon,
} from "sebikostudio-icons";
import Beaver from "../assets/avatar.png";
import CodeFormatter from "./CodeFormatter";

const ParagraphPage = () => {
  const codeString1 = `  
import { Paragraph } from "blocksin-system";

<Paragraph size="large" weight="bold">
  This is a large, bold paragraph.
</Paragraph>
              `;
  const dialogExample = `<Dialog.Root>
  <Dialog.Trigger asChild>
    <Button variant="outline">Open dialog</Button>
  </Dialog.Trigger>
  <Dialog.Portal>
    <Dialog.Overlay className="DialogOverlay" />
    <Dialog.Content className="DialogContent">
      <Flex direction="column" gap={200}>
        <Dialog.Title asChild>
          <Heading level={3} weight="bold">Review changes</Heading>
        </Dialog.Title>
        <Dialog.Description asChild>
          <Paragraph>Review your changes before saving.</Paragraph>
        </Dialog.Description>
        <Flex justify="end" gap={100}>
          <Dialog.Close asChild>
            <Button>Done</Button>
          </Dialog.Close>
        </Flex>
      </Flex>
    </Dialog.Content>
  </Dialog.Portal>
</Dialog.Root>`;
  const errorExample = `<Paragraph className="Error" role="alert" aria-live="assertive">
  Your changes could not be saved. Please try again.
</Paragraph>`;
  const scope = {
    Dialog,
    Separator,
    Avatar,
    Beaver,
    Button,
    Cross2Icon,
    SizeIcon,
    CopyIcon,
    IconButton,
    Flex,
    Heading,
    BellIcon,
    Paragraph,
    Card,
  };

  const columns = [
    {
      Header: "Property",
      accessor: "property",
      Cell: (value) => <CodeHighlight text={value} />,
    },
    { Header: "Type", accessor: "type" },
    { Header: "Description", accessor: "description" },
    { Header: "Default", accessor: "default" },
  ];

  const data = [
    {
      property: "Native paragraph props",
      type: 'React.ComponentPropsWithoutRef<"p">',
      description: "Supports id, title, style, role, aria-*, data-*, tabIndex, hidden, event handlers, and other native paragraph props. No accessibility attributes are added by default.",
      default: "-",
    },
    {
      property: "ref",
      type: "React.Ref<HTMLParagraphElement>",
      description: "Forwarded to the rendered <p> element.",
      default: "-",
    },
    {
      property: "className",
      type: "String (optional)",
      description:
        "Custom CSS classes merged with the generated size and weight classes.",
      default: "-",
    },
    {
      property: "size",
      type: "String (optional)",
      description:
        "Specifies the size of the text (small, medium, large, etc.). Default is medium.",
      default: "medium",
    },
    {
      property: "weight",
      type: "String (optional)",
      description:
        "Sets the font weight of the text (regular, bold, etc.). Default is regular.",
      default: "regular",
    },
    {
      property: "children",
      type: "Node (required)",
      description: "The text content to be displayed inside the paragraph.",
      default: "-",
    },
  ];

  //
  return (
    <>
      <Flex direction={"column"} id="IconButton" className={"WebPageBody"}>
        <Section>
          <Heading level={2} weight="bold">
            Paragraph Component
          </Heading>
          <Paragraph size="large">
            The Paragraph component is a React component for rendering text
            content. It provides options to customize the size and weight of the
            text, making it a versatile choice for displaying paragraphs or
            other textual elements in your application.
          </Paragraph>
          <Flex className="ComponentPreview" direction="column">
            <Paragraph size="large" weight="bold">
              This is a large, bold paragraph.
            </Paragraph>
          </Flex>
          <Heading level={3} weight="bold">
            Usage
          </Heading>
          <CodeFormatter language="" codeString={codeString1} />
        </Section>

        <Section>
          <Heading level={3} weight="bold">
            Variants
          </Heading>
          <Paragraph size="large">
            Combine small, medium, or large text with regular or bold weight.
            The default is medium, regular text.
          </Paragraph>
          <Flex className="ComponentPreview" direction="column">
            <Paragraph size="large" weight="bold">
              This is a large, bold paragraph.
            </Paragraph>
            <Paragraph size="medium" weight="bold">
              This is a medium, bold paragraph.
            </Paragraph>
            <Paragraph size="small" weight="bold">
              This is a small, bold paragraph.
            </Paragraph>
            <Paragraph size="large">
              This is a large, regular paragraph.
            </Paragraph>
            <Paragraph size="medium">
              This is a medium, regular paragraph.
            </Paragraph>
            <Paragraph size="small">
              This is a small, regular paragraph.
            </Paragraph>
          </Flex>
        </Section>

        <Section>
          <Heading level={3} weight="bold">
            Properties
          </Heading>

          <Table
            fluid
            columns={columns}
            data={data}
            pageSize={10}
            sorting={false}
            fullBorder
            large
          />
        </Section>

        <Section>
          <Heading level={3} weight="bold">Radix dialog description</Heading>
          <Paragraph>
            Import * as Dialog from "@radix-ui/react-dialog". With Description
            asChild, Paragraph receives Radix's generated id so the dialog's
            aria-describedby references the paragraph.
          </Paragraph>
          <ReactLive scope={scope}>{dialogExample}</ReactLive>
          <Heading level={3} weight="bold">Error message</Heading>
          <Paragraph>
            Supply role and aria-live explicitly when an error should be announced.
            Paragraph adds no role, live region, tabIndex, or accessible label by default.
            Size and weight only control typography and are not forwarded to the DOM.
          </Paragraph>
          <ReactLive scope={scope}>{errorExample}</ReactLive>
        </Section>

        <Section>
          <Heading level={3} weight="bold">
            Playground
          </Heading>
          <Paragraph size="large">
            Experience real-time component interaction with React! This powerful
            feature empowers non-technical users—designers, project managers,
            brand or content specialists—to seamlessly engage with components.
            They can modify component content on the fly, ensuring comprehensive
            validation against accessibility, localization, and content design
            requirements. This hands-on approach ensures the design aligns
            perfectly with all necessary criteria, fostering a more
            collaborative and efficient development process.
          </Paragraph>

          <ReactLive scope={scope}>
            {`
<Flex direction="row" gap={100}>
  <Paragraph size="large" weight="bold">
    This is a large, bold paragraph.
  </Paragraph>
</Flex>
      `}
          </ReactLive>
        </Section>

        <Section>
          <Heading level={3} weight="bold">
            Design Patterns
          </Heading>
          <Paragraph size="large">
            Design patterns with paragraphs, like small or large, can be used to
            create visually distinct text blocks for different types of content,
            such as short descriptions or longer paragraphs, ensuring a clear
            hierarchy and improved readability in product interfaces.
          </Paragraph>
          <ReactLive scope={scope}>
            {`
<Flex direction="row" gap={100}>
  <Card direction="column" gap={200} align="center" style={{minWidth: "290px"}}>
    <Flex gap={100} fluid>
      <Button fluid variant="ghost">Learn more</Button>
      <Button fluid>Get Started</Button>
    </Flex>
    <Paragraph size="small">
      Cancel anytime!
    </Paragraph>
  </Card>
</Flex>
            `}
          </ReactLive>
        </Section>
      </Flex>
    </>
  );
};

export default ParagraphPage;
